import { EnvironmentProviders } from '@angular/core';
import { convertDateStringFormatsToDate } from '@core/misc/misc.utils';
import { AuthState } from '@core/modules/auth/state/auth.state';
import { UserState } from '@core/modules/user/state/user.state';
import { STORE_COMMON_STORE_ITEM_NAME } from '@core/store/store.const';
import { LANGUAGES } from '@data/locale.data';
import { PERSISTENT_STATES_REGISTRY } from '@data/persistent.states.registry';
import { environment } from '@env/environment';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { NgxsStoragePluginOptions, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { AppPersistedStateModel } from './state/app.models';
import { AppPersistedState } from './state/app.persisted.state';
import { AppState } from './state/app.state';

type StorageMigration = NonNullable<NgxsStoragePluginOptions['migrations']>[number];

/** Root states registered at bootstrap. Feature states are provided lazily by their routes. */
export const APP_ROOT_STATES = [AppState, AuthState, UserState, AppPersistedState];

/**
 * Rebuilds the persisted `_common` slice from defaults, keeping only the user-chosen language
 * (when it is still a supported one). Everything else is dropped on purpose: bumping
 * `persistedStateStorageVersion` is how a deploy invalidates stale persisted state.
 */
export const migrateCommonPersistedState = (
  stored: Partial<AppPersistedStateModel> | null | undefined
): AppPersistedStateModel => {
  const migrated = new AppPersistedStateModel();
  if (stored?.language && stored.language in LANGUAGES) migrated.language = stored.language;
  return migrated;
};

/**
 * The storage plugin applies a migration only when the stored version EQUALS `version` — there is
 * no "older than" matching. A single `current - 1` entry therefore skipped everyone who missed a
 * release or more: their stale slice was loaded untouched. One entry per past version closes that
 * gap; `0` also catches a slice with no `_version` at all (the plugin defaults a missing version to 0).
 */
export const buildCommonStateMigrations = (currentVersion: number): StorageMigration[] =>
  Array.from(Array(currentVersion).keys(), (version) => ({
    version,
    versionKey: '_version',
    key: STORE_COMMON_STORE_ITEM_NAME,
    migrate: migrateCommonPersistedState
  }));

/*
 * NGXS plugins are FEATURES of `provideStore` and must be passed as its arguments — placed as
 * sibling providers they are only picked up by accident when the devtools plugin is present
 * (i.e. in development), and every production-flag build shipped with dead plugins: `Navigate`
 * dispatches were silently ignored and lazily provided states never registered.
 *
 * Plugins run in registration order, so the devtools plugin goes last: its `@@INIT` snapshot is
 * then taken after the storage plugin has hydrated the persisted slices.
 */
export const provideAppStore = (): EnvironmentProviders =>
  provideStore(
    APP_ROOT_STATES,
    { developmentMode: !environment.production },
    withNgxsRouterPlugin(),
    withNgxsFormPlugin(),
    withNgxsStoragePlugin({
      keys: PERSISTENT_STATES_REGISTRY,
      afterDeserialize: (obj) => convertDateStringFormatsToDate(obj),
      migrations: buildCommonStateMigrations(environment.persistedStateStorageVersion)
    }),
    ...(environment.production ? [] : [withNgxsReduxDevtoolsPlugin()])
  );
